import { VStack } from "@chakra-ui/react";
import { auth } from "../../../../../core/auth";
import { env } from "../../../../../env";
import PatientProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import { FamilyInformationBlock } from "./components/FamilyInformationBlock";
import { InformationInlineEditor } from "./components/InformationInlineEditor";
import {
  InformationBlock,
  InformationEditableRow,
  InformationReadonlyRow,
} from "./components/InformationRows";
import { usePatientProfileInformationController } from "./PatientProfileInformationSection.hooks";
import type { PatientInformationField } from "./patientInformation.types";
import { getDraftDisplayValue } from "./patientInformation.utils";

type Props = {
  actions: PatientProfileBridgeActions;
  patient: PatientProfilePatient;
};

export default function PatientProfileInformationSection(props: Props) {
  const { patient } = props;
  const controller = usePatientProfileInformationController(patient, props.actions);
  const authState = auth.getAuthState();
  const isOnboardingAgency =
    authState.type === "authenticated" && authState.data.agency.id === env.MEDFLYT_AGENCY_ID;
  const renderEditableRow = (field: PatientInformationField, label: string) => (
    <InformationEditableRow
      activeField={controller.activeField}
      field={field}
      isEditing={controller.isEditing}
      label={label}
      value={getDraftDisplayValue(patient, controller.draft, field)}
      onActivate={controller.activateField}
      onCancel={controller.cancelField}
      onCommit={controller.commitField}
    >
      {controller.draft === null ? null : (
        <InformationInlineEditor
          draft={controller.draft}
          field={field}
          patient={patient}
          onChange={controller.setDraft}
        />
      )}
    </InformationEditableRow>
  );

  return (
    <VStack alignItems="stretch" spacing={8}>
      {controller.canEdit ? (
        <PatientProfileEditModeButton
          isDisabled={controller.isSaving}
          isEditing={controller.isEditing}
          label="Edit"
          onClick={controller.toggleEditing}
        />
      ) : null}
      <InformationBlock>
        <InformationReadonlyRow
          isEditing={controller.isEditing}
          label="Status"
          value={patient.status}
          onClick={controller.openChangeStatus}
        />
        {renderEditableRow("source", "Source")}
        {renderEditableRow("medicareNumber", "Medicare Number")}
        {renderEditableRow("medicaidNumber", "Medicaid Number")}
        {renderEditableRow("wageParityNumber", "Wage Parity")}
        {renderEditableRow("generalNotes", "General Notes")}
      </InformationBlock>

      <InformationBlock title="Assignees">
        {renderEditableRow("socialWorker", "Social Worker")}
        {renderEditableRow("assignedIntake", "Assigned Intake")}
        {renderEditableRow("assignedSalesRep", "Assigned Sales Rep")}
        {renderEditableRow("assignedDPS", "Assigned DPS")}
      </InformationBlock>

      <InformationBlock title="Patient Information">
        {renderEditableRow("ssn", "SSN")}
        {renderEditableRow("email", "Email")}
        {renderEditableRow("patientHIClaim", "Patient HI Claim")}
      </InformationBlock>

      <InformationBlock title="Patient Preferences">
        {renderEditableRow("preferredHHAGender", "HHA Gender")}
        {renderEditableRow("smoking", "Smoking")}
        {renderEditableRow("pets", "Pets")}
        {renderEditableRow("kosherHome", "Kosher")}
        {renderEditableRow("kidsInHouse", "Kids In House")}
      </InformationBlock>

      <FamilyInformationBlock
        actions={props.actions}
        activeFamilyField={controller.activeFamilyField}
        familyMembers={controller.familyDraft ?? patient.familyInformation}
        isEditing={controller.isEditing}
        isOnboardingAgency={isOnboardingAgency}
        onActivateField={controller.setActiveFamilyField}
        onAddFamilyMember={controller.addFamilyMember}
        onCancelField={controller.cancelFamilyField}
        onCommitField={controller.commitFamilyField}
        onUpdateFamilyMember={controller.updateFamilyMember}
      />
    </VStack>
  );
}
